/**
 * Copyright 2025-2026 Nordcloud Oy or its affiliates. All Rights Reserved.
 */

import React, { useCallback, useEffect, useMemo, useState } from "react";
import type { ResourceWithId } from "~/components/resourceGroup/types";
import { findDuplicateResourceIds } from "~/components/resourceGroup/utils";
import { isEmpty } from "~/tools";

export type DuplicateResourceState = Readonly<{
  duplicateResourceIds: Set<ResourceWithId["id"]>;
  highlightedDuplicateId: string | null;
  allAddedResourceIds: Set<ResourceWithId["id"]>;
  isHighlighted: (id: string | undefined) => boolean;
  onDuplicateIconClick: (resourceId: string) => void;
  onGroupLoad: (groupId: string, groupResources: ResourceWithId[]) => void;
}>;

export const useDuplicatedResources = ({
  resourceCollections = [],
  actionChangeKey,
}: {
  resourceCollections?: ResourceWithId[][];
  actionChangeKey?: React.Key;
}): DuplicateResourceState => {
  const [accumulatedGroupResources, setAccumulatedGroupResources] = useState<
    Record<string, ResourceWithId[]>
  >({});

  useEffect(() => {
    setAccumulatedGroupResources({});
  }, [actionChangeKey]);

  const onGroupLoad = useCallback(
    (groupId: string, groupResources: ResourceWithId[]) => {
      setAccumulatedGroupResources((prev) => {
        if (prev[groupId]?.length === groupResources.length) {
          return prev;
        }
        return { ...prev, [groupId]: groupResources };
      });
    },
    []
  );

  const [highlightedDuplicateId, setHighlightedDuplicateId] = useState<
    string | null
  >(null);

  const allAddedResources = useMemo(() => {
    const dynamicGroups = Object.values(accumulatedGroupResources);
    return [...resourceCollections, ...dynamicGroups].flat();
  }, [resourceCollections, accumulatedGroupResources]);

  const allAddedResourceIds = useMemo(
    () => new Set(allAddedResources.map((resource) => resource.id)),
    [allAddedResources]
  );

  const duplicateResourceIds = useMemo(() => {
    if (isEmpty(allAddedResources)) {
      return new Set<ResourceWithId["id"]>();
    }
    return findDuplicateResourceIds(allAddedResources);
  }, [allAddedResources]);

  const onDuplicateIconClick = useCallback((resourceId: string) => {
    setHighlightedDuplicateId((prev) =>
      prev === resourceId ? null : resourceId
    );
  }, []);

  useEffect(() => {
    if (!highlightedDuplicateId) {
      return;
    }

    const handleOutsideClick = () => setHighlightedDuplicateId(null);
    document.addEventListener("click", handleOutsideClick);
    return () => document.removeEventListener("click", handleOutsideClick);
  }, [highlightedDuplicateId]);

  const isHighlighted = useCallback(
    (id: string | undefined) => !!id && highlightedDuplicateId === id,
    [highlightedDuplicateId]
  );

  return {
    duplicateResourceIds,
    highlightedDuplicateId,
    allAddedResourceIds,
    isHighlighted,
    onDuplicateIconClick,
    onGroupLoad,
  };
};
